import { IDLE_BASE_INTERVAL_MS, IDLE_MAX_QUEUE, Family } from '../config';
import { Grid } from './Grid';
import { UidAllocator } from './UidAllocator';
import { getRandomFragmentByFamily, ElementInstance } from '../data/elements';

/**
 * A Generator describes an idle element production source.
 */
export interface Generator {
  family: Family;
  level: number; // Higher level = faster generation
  lastGenerateTime: number;
}

/**
 * IdleSystem manages offline/idle element generation via generators.
 * Generators continuously produce fragments while the game is running or offline.
 */
export class IdleSystem {
  private grid: Grid;
  private generators: Generator[];
  private lastTickTime: number;
  private uids: UidAllocator;

  constructor(grid: Grid, uids?: UidAllocator) {
    this.grid = grid;
    this.generators = [];
    this.lastTickTime = Date.now();
    this.uids = uids ?? new UidAllocator(1);

    // Start with one default Fire generator at level 1
    this.addGenerator(Family.Fire);
  }

  /**
   * Add a new generator for a specific family.
   */
  addGenerator(family: Family): void {
    const generator: Generator = {
      family,
      level: 1,
      lastGenerateTime: Date.now(),
    };
    this.generators.push(generator);
  }

  /**
   * Update all generators and spawn fragments if enough time has passed.
   * Called each frame; spawns elements in empty grid cells.
   */
  update(now: number): ElementInstance[] {
    const spawned: ElementInstance[] = [];

    // Count how many elements are already on the grid
    const currentCount = this.grid.getAll().length;
    const spaceAvailable = IDLE_MAX_QUEUE - currentCount;

    if (spaceAvailable <= 0) {
      // Grid is at or over max queue size, don't spawn
      return spawned;
    }

    // Check each generator
    for (const generator of this.generators) {
      const intervalMs = this.getGeneratorInterval(generator);
      const elapsed = now - generator.lastGenerateTime;

      if (elapsed >= intervalMs) {
        // Time to generate
        const emptyCell = this.grid.findEmpty();
        if (emptyCell) {
          // Spawn a random fragment of the generator's family
          const fragmentDef = getRandomFragmentByFamily(generator.family);
          const element: ElementInstance = {
            uid: this.uids.mint(),
            defId: fragmentDef.id,
            col: emptyCell.col,
            row: emptyCell.row,
            age: 0,
            corrupted: false,
          };

          this.grid.set(emptyCell.col, emptyCell.row, element);
          spawned.push(element);

          // Update generator's last generate time
          // If multiple intervals have passed, count only one and advance by one interval
          generator.lastGenerateTime = now - (elapsed % intervalMs);
        } else {
          // No empty cells, but still advance the timer
          generator.lastGenerateTime = now;
        }
      }
    }

    this.lastTickTime = now;
    return spawned;
  }

  /**
   * Calculate offline rewards based on how long the game was closed.
   * Simulates generator activity and places fragments on the grid.
   */
  calculateOfflineRewards(offlineDurationMs: number): ElementInstance[] {
    const rewards: ElementInstance[] = [];

    // Simulate each generator
    for (const generator of this.generators) {
      const intervalMs = this.getGeneratorInterval(generator);
      const generatedCount = Math.floor(offlineDurationMs / intervalMs);

      for (let i = 0; i < generatedCount; i++) {
        const emptyCell = this.grid.findEmpty();
        if (!emptyCell) {
          // Grid full, stop spawning
          break;
        }

        const fragmentDef = getRandomFragmentByFamily(generator.family);
        const element: ElementInstance = {
          uid: this.uids.mint(),
          defId: fragmentDef.id,
          col: emptyCell.col,
          row: emptyCell.row,
          age: 0,
          corrupted: false,
        };

        this.grid.set(emptyCell.col, emptyCell.row, element);
        rewards.push(element);
      }

      // If we hit the grid limit, don't process remaining generators
      if (rewards.length >= IDLE_MAX_QUEUE) {
        break;
      }
    }

    // Reset generator timers now that we've returned
    const now = Date.now();
    for (const generator of this.generators) {
      generator.lastGenerateTime = now;
    }

    return rewards;
  }

  /**
   * Check if a generator for the given family already exists.
   */
  hasGenerator(family: Family): boolean {
    return this.generators.some((g) => g.family === family);
  }

  /**
   * Get all generators.
   */
  getGenerators(): Generator[] {
    return this.generators;
  }

  /**
   * Calculate the spawn interval for a generator based on its level.
   * Higher level = faster (shorter interval).
   */
  private getGeneratorInterval(generator: Generator): number {
    // Base interval scaled inversely by level
    // Level 1: IDLE_BASE_INTERVAL_MS
    // Level 2: IDLE_BASE_INTERVAL_MS * 0.7
    // Level 3: IDLE_BASE_INTERVAL_MS * 0.49
    // etc.
    const levelFactor = Math.pow(0.7, generator.level - 1);
    return IDLE_BASE_INTERVAL_MS * levelFactor;
  }

  /**
   * Upgrade a generator to the next level (for power-ups, etc).
   */
  upgradeGenerator(index: number): boolean {
    if (index < 0 || index >= this.generators.length) {
      return false;
    }

    this.generators[index].level++;
    return true;
  }

  /**
   * Get the next UID (for testing/debugging).
   */
  getNextUid(): number {
    return this.uids.peek();
  }
}
